import React from 'react';
import { useTranslation } from 'react-i18next';
import { Target, Lock } from 'lucide-react';

/**
 * LearningObjectivesStepShell
 * Reusable layout shell for Step 2 (Learning Objectives)
 *
 * Props:
 * - objectives: array - list of objective objects {id, text, bloomLevel}
 * - headerTitle: string - main header title
 * - headerSubtitle: string - header subtitle text
 * - onEdit: function - handler for inline editing (path, value)
 * - importMode: boolean - whether we're in import mode (shows source badges)
 * - children: React node - additional controls (generate button, pending objective, etc.)
 */
export default function LearningObjectivesStepShell({
  objectives = [],
  headerTitle,
  headerSubtitle,
  onEdit = null,
  importMode = false,
  children
}) {
  const { t } = useTranslation();
  const defaultHeaderTitle = headerTitle || t('step2.defineObjectives');
  const defaultHeaderSubtitle = headerSubtitle || t('step2.whatShouldStudentsDo');
  const hasObjectives = objectives && objectives.length > 0;

  return (
    <div className="w-full" data-testid="objectives-step-shell">
      <div className="flex-1 p-8">
        <div className="max-w-2xl mx-auto">
          <div className="space-y-6">
            {/* Learning objectives list */}
            {hasObjectives && (
              <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-teal-100 space-y-3" data-testid="objectives-list">
                {objectives.map((objective, index) => {
                  const isProtected = objective.isProtected || false;
                  const linkedActivityCount = objective.linkedActivityCount || 0;
                  const objectiveSource = objective.source; // 'imported', 'generated', or undefined/null

                  return (
                    <div
                      key={objective.id || index}
                      className={`flex items-start gap-3 p-4 rounded-xl group transition-shadow ${
                        isProtected
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200'
                          : 'bg-gradient-to-r from-teal-50 to-orange-50 hover:shadow-md'
                      }`}
                      data-testid={`objective-item-${index}`}
                    >
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isProtected
                          ? 'bg-gradient-to-br from-blue-500 to-indigo-500 text-white'
                          : 'bg-gradient-to-br from-teal-500 to-orange-500 text-white'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div
                            data-testid={`objective-display-${index}`}
                            className="text-gray-800 font-medium objective-text hover:bg-white/50 px-2 py-1 rounded transition-colors cursor-text flex-1 min-w-0"
                            contentEditable={!!onEdit}
                            suppressContentEditableWarning
                            onBlur={onEdit ? (e) => onEdit(`learningObjectives.${index}.text`, e.target.textContent) : undefined}
                          >
                            {objective.text}
                          </div>
                          {/* Source badge - only show in import mode */}
                          {importMode && objectiveSource === 'imported' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              {t('step2.imported')}
                            </span>
                          )}
                          {importMode && objectiveSource === 'generated' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              {t('step2.generated')}
                            </span>
                          )}
                        </div>
                        {isProtected && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                            <Lock size={12} />
                            <span>{t('step2.protected')} - {linkedActivityCount} {linkedActivityCount === 1 ? t('step2.activity') : t('step2.activities')} {t('step2.linked')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Additional controls (generate button, pending objective, chat, etc.) */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
